import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { ChipFiltersProps, FilterValues } from './chip-filters.types';
import Chip from './chip.component';
import styles from './chip-filters.module.scss';

export default function ChipFilters({
  defs,
  value,
  onChange,
  className,
  hideClearAll
}: ChipFiltersProps) {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState<string | null>(null);

  const defKeys = new Set(defs.map((d) => d.key));

  const activeCount = Object.entries(value).filter(([k, v]) => {
    if (!defKeys.has(k)) return false;
    if (!v) return false;
    if (Array.isArray(v)) return v.length > 0;
    return v !== '';
  }).length;

  const handleChange = useCallback(
    (key: string, next: string | string[] | undefined) => {
      onChange({ ...value, [key]: next });
    },
    [value, onChange]
  );

  const handleClearAll = useCallback(() => {
    const cleared: FilterValues = {};
    for (const def of defs) cleared[def.key] = undefined;
    onChange(cleared);
  }, [defs, onChange]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && openKey) setOpenKey(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [openKey]);

  return (
    <div className={clsx(styles.row, className)}>
      {defs.map((def) => (
        <Chip
          key={def.key}
          def={def}
          value={value}
          isOpen={openKey === def.key}
          onOpen={() => setOpenKey(def.key)}
          onClose={() => setOpenKey(null)}
          onChange={handleChange}
        />
      ))}

      {activeCount > 0 && !hideClearAll && (
        <button
          type="button"
          className={styles.clearAll}
          onClick={handleClearAll}
          aria-label={t('chip-filters.clearAll', { count: activeCount })}
        >
          <i className="ki-duotone ki-eraser fs-7 text-danger">
            <span className="path1" />
            <span className="path2" />
          </i>
          {t('chip-filters.clearAll', { count: activeCount })}
        </button>
      )}
    </div>
  );
}
